import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { formatCurrency } from '../../utils/Currency';

const CLASS_LABELS = {
  FIXED_INCOME: 'Renda Fixa',
  VARIABLE_INCOME: 'Renda Variavel',
  CRYPTO: 'Cripto',
  CASH: 'Caixa',
  OTHER: 'Outros',
};

const INSTRUMENT_LABELS = {
  CDB: 'CDB',
  LCI: 'LCI',
  LCA: 'LCA',
  CRI: 'CRI',
  CRA: 'CRA',
  LC: 'LC',
  DEBENTURE: 'Debenture',
  TREASURY_SELIC: 'Tesouro Selic',
  TREASURY_IPCA: 'Tesouro IPCA',
  TREASURY_PREFIXED: 'Tesouro Pre',
  STOCK: 'Acoes',
  STOCK_BRL: 'Acoes BR',
  STOCK_USA: 'Acoes USA',
  ETF: 'ETF',
  FII: 'FII',
  BDR: 'BDR',
  CRYPTO: 'Cripto',
  FIAT: 'Fiat',
};

const CLASS_COLORS = {
  FIXED_INCOME: '#2196F3',
  VARIABLE_INCOME: '#4CAF50',
  CRYPTO: '#FF9800',
  CASH: '#9C27B0',
  OTHER: '#607D8B',
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 14,
  fontWeight: 500,
}));

const StyledValueText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
  fontSize: 16,
  fontWeight: 700,
}));

function PieCenterLabel({ label, value }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <>
      <StyledText x={left + width / 2} y={top + height / 2 - 8}>
        {label}
      </StyledText>
      <StyledValueText x={left + width / 2} y={top + height / 2 + 10}>
        {value}
      </StyledValueText>
    </>
  );
}

const DistributionChart = ({ positions, currency, loading, showValues }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const { classData, instrumentByClassData, total } = useMemo(() => {
    if (!positions || positions.length === 0) {
      return { classData: [], instrumentByClassData: [], total: 0 };
    }

    const classTotals = {};
    const instrumentByClass = {};
    let totalValue = 0;

    positions.forEach((pos) => {
      const value = parseFloat(pos.CurrentValue?.[currency]) || 0;
      if (value <= 0) return;

      totalValue += value;

      const assetClass = pos.Asset?.Class || 'OTHER';
      const instrumentType = pos.Asset?.InstrumentType || 'OTHER';

      classTotals[assetClass] = (classTotals[assetClass] || 0) + value;

      if (!instrumentByClass[assetClass]) {
        instrumentByClass[assetClass] = {};
      }
      instrumentByClass[assetClass][instrumentType] =
        (instrumentByClass[assetClass][instrumentType] || 0) + value;
    });

    const classData = Object.entries(classTotals)
      .map(([key, value]) => ({
        id: key,
        value,
        label: CLASS_LABELS[key] || key,
        color: CLASS_COLORS[key] || '#607D8B',
        percentage: totalValue > 0 ? (value / totalValue) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    const opacityLevels = [1, 0.8, 0.6, 0.5, 0.4, 0.35, 0.3];
    const instrumentByClassData = [];

    classData.forEach((classItem) => {
      const instruments = instrumentByClass[classItem.id] || {};
      const sortedInstruments = Object.entries(instruments)
        .sort((a, b) => b[1] - a[1]);

      sortedInstruments.forEach(([instrumentKey, instrumentValue], idx) => {
        const classTotal = classItem.value;
        const opacity = opacityLevels[Math.min(idx, opacityLevels.length - 1)];

        instrumentByClassData.push({
          id: `${classItem.id}-${instrumentKey}`,
          value: instrumentValue,
          label: INSTRUMENT_LABELS[instrumentKey] || instrumentKey,
          color: hexToRgba(classItem.color, opacity),
          percentage: classTotal > 0 ? (instrumentValue / classTotal) * 100 : 0,
          parentClass: classItem.id,
          parentLabel: classItem.label,
        });
      });
    });

    return { classData, instrumentByClassData, total: totalValue };
  }, [positions, currency]);

  const formatTotal = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toFixed(0);
  };

  if (loading) {
    return (
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          boxShadow: theme.shadows[2],
          height: '100%',
        }}
      >
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="circular" width={250} height={250} sx={{ mx: 'auto', my: 2 }} />
        <Skeleton variant="text" width="100%" height={24} />
      </Paper>
    );
  }

  const innerRadius = isSmall ? 42 : 50;
  const middleRadius = isSmall ? 82 : 105;
  const outerRadius = isSmall ? 110 : 140;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        boxShadow: theme.shadows[2],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
        Distribuicao do Patrimonio
      </Typography>

      {classData.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.text.secondary,
          }}
        >
          <Typography>Sem dados para exibir</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: { xs: 300, sm: 380 },
              overflow: 'hidden',
            }}
          >
            <PieChart
              series={[
                {
                  innerRadius,
                  outerRadius: middleRadius,
                  data: classData,
                  arcLabel: (item) => `${item.percentage.toFixed(0)}%`,
                  valueFormatter: (item) => {
                    if (!showValues) return `${item.percentage?.toFixed(1)}%`;
                    return `${formatCurrency(currency, item.value)} (${item.percentage?.toFixed(1)}%)`;
                  },
                  highlightScope: { fade: 'global', highlight: 'item' },
                  highlighted: { additionalRadius: 3 },
                  cornerRadius: 3,
                },
                {
                  innerRadius: middleRadius + 2,
                  outerRadius: outerRadius,
                  data: instrumentByClassData,
                  arcLabel: (item) => item.percentage > 15 ? `${item.label}` : '',
                  valueFormatter: (item) => {
                    if (!showValues) return `${item.label}: ${item.percentage?.toFixed(1)}% de ${item.parentLabel}`;
                    return `${item.label}: ${formatCurrency(currency, item.value)} (${item.percentage?.toFixed(1)}% de ${item.parentLabel})`;
                  },
                  highlightScope: { fade: 'global', highlight: 'item' },
                  highlighted: { additionalRadius: 3 },
                  cornerRadius: 2,
                  arcLabelMinAngle: 20,
                },
              ]}
              margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                width: '100%',
                maxWidth: '100%',
                [`& .${pieArcLabelClasses.root}`]: {
                  fontSize: '11px',
                  fontWeight: 500,
                },
              }}
            >
              <PieCenterLabel
                label="Total"
                value={showValues ? formatTotal(total) : '--'}
              />
            </PieChart>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'center',
              mt: 1,
            }}
          >
            {classData.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 11,
                  color: theme.palette.text.secondary,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 1,
                    backgroundColor: item.color,
                  }}
                />
                <span>
                  {item.label}: {item.percentage.toFixed(1)}%
                </span>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default DistributionChart;
